import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { AnnotationEditor } from '@markerjs/markerjs-ui'
import {
  AnnotationState,
  ArrowMarker,
  ArrowMarkerEditor,
  MarkerEditorEventData,
} from '@markerjs/markerjs3'
import { MarkerOptionType, MarkersType } from 'src/app/modules/configuration/interface/marker'

@Component({
  selector: 'app-marker',
  standalone: true,
  templateUrl: './marker.component.html',
  styleUrls: ['./marker.component.scss'],
})
export class MarkerComponent implements AfterViewInit, OnChanges, OnDestroy {
  constructor(
    private readonly renderer: Renderer2,
    private cdr: ChangeDetectorRef
  ) {}

  @ViewChild('markerImage') markerImageElement!: ElementRef<HTMLImageElement>
  @ViewChild('imageContainer') imageContainer!: ElementRef<HTMLImageElement>
  @ViewChild('container') containerDivElement!: ElementRef<HTMLDivElement>

  @Input() imageSrc: string = ''
  @Input() imageAlt: string = ''
  @Input() imageWidth: string = 'auto'
  @Input() imageHeight: string = 'auto'
  @Input() theme: 'light' | 'dark' = 'light'
  @Input() state: AnnotationState | null = null

  private possibleMarkers: MarkersType[] = [
    'Rectangle',
    'Highlight',
    'Ellipse',
    'Cover (filled rectangle)',
    'Ellipse (filled)',
    'Arrow',
    'Line',
    'Measure',
    'Curve',
    'Text',
    'Callout',
    'Captioned frame',
    'Freehand',
    'Highlighter',
    'Polygon',
    'Smiley',
    'Disappointed face',
    'Crying face',
    'Angry face',
    'Exclamation mark',
    'Question mark',
    'Check mark',
    'Cross mark',
    'Heart',
    'Thumbs up',
    'Thumbs down',
  ]
  @Input() options: MarkerOptionType = {
    toolbar: { markers: [], showDoneButton: true, showCancelButton: true },
  }
  markerArea: AnnotationEditor | null = null

  @Output() stateChange = new EventEmitter<AnnotationState>()
  @Output() stateDeleteMarker = new EventEmitter<CustomEvent<MarkerEditorEventData>>()
  @Output() stateSelectMarker = new EventEmitter<CustomEvent<MarkerEditorEventData>>()
  @Output() stateCreateMarker = new EventEmitter<CustomEvent<MarkerEditorEventData>>()
  @Output() whenRender = new EventEmitter<void>()

  private isInitialized = false
  private currentState: AnnotationState | null = null

  ngAfterViewInit(): void {
    this.initializeMarker()
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.handleImageSrcChange(changes)
    this.handleStateChange(changes)
    this.handleDimensionChanges(changes)
    this.handleThemeChange(changes)
  }

  ngOnDestroy(): void {
    this.destroyMarker()
  }

  private handleImageSrcChange(changes: SimpleChanges): void {
    if (!this.isImageSrcChanged(changes)) {
      return
    }

    this.resetForNewImage()
    this.waitForImageLoad()
  }

  private isImageSrcChanged(changes: SimpleChanges): boolean {
    return (
      changes['imageSrc']?.previousValue !== changes['imageSrc']?.currentValue &&
      changes['imageSrc']?.currentValue
    )
  }

  private resetForNewImage(): void {
    this.destroyMarker()
    this.isInitialized = false
    this.showImage = true
    this.currentState = null
    this.cdr.detectChanges()
  }

  private waitForImageLoad(): void {
    setTimeout(() => {
      if (!this.markerImageElement?.nativeElement) {
        return
      }

      this.markerImageElement.nativeElement.onload = () => {
        this.initializeMarker()
      }

      if (this.markerImageElement.nativeElement.complete) {
        this.initializeMarker()
      }
    })
  }

  private handleStateChange(changes: SimpleChanges): void {
    if (!changes['state'] || !this.state) {
      return
    }

    this.currentState = this.state

    if (this.isInitialized && this.markerArea) {
      this.markerArea.restoreState(this.state)
    }
  }

  private handleDimensionChanges(changes: SimpleChanges): void {
    const hasDimensionChanges = changes['imageWidth'] || changes['imageHeight']

    if (hasDimensionChanges && this.isInitialized) {
      this.reloadMarker()
    }
  }

  private handleThemeChange(changes: SimpleChanges): void {
    if (changes['theme'] && this.isInitialized) {
      this.reloadMarker()
    }
  }

  private destroyMarker(): void {
    if (this.markerArea) {
      this.saveCurrentState()

      this.markerArea.markerArea.removeEventListener('areastatechange', this.areaStateChangeHandler)
      this.markerArea.markerArea.removeEventListener('markercreating', this.markerCreatingHandler)
      this.markerArea.markerArea.removeEventListener(
        'markerbeforedelete',
        this.markerBeforeDeleteHandler
      )
      this.markerArea.markerArea.removeEventListener('markerselect', this.selectMarkerHandler)

      if (
        this.containerDivElement?.nativeElement &&
        this.markerArea.parentNode === this.containerDivElement.nativeElement
      ) {
        this.containerDivElement.nativeElement.removeChild(this.markerArea)
      }
      this.markerArea = null
    }
    this.isInitialized = false
  }

  private initializeMarker(): void {
    if (
      this.isInitialized ||
      !this.containerDivElement?.nativeElement ||
      !this.markerImageElement?.nativeElement
    ) {
      return
    }

    // Clean up any existing marker
    this.destroyMarker()

    const editor = new AnnotationEditor()
    editor.targetImage = this.markerImageElement.nativeElement

    this.setOptions(editor)

    this.showImage = false
    editor.theme = this.theme
    editor.markerArea.registerMarkerType(ArrowMarker, ArrowMarkerEditor)

    // Store handlers as properties to ensure we remove the same ones
    this.areaStateChangeHandler = (ev: CustomEvent) => {
      const newState = ev.detail.markerArea.getState()
      this.currentState = newState // Update current state
      this.stateChange.emit(newState)
    }

    this.markerCreatingHandler = (ev: CustomEvent) => {
      ev.detail.markerEditor.notes = crypto.randomUUID()
      this.stateCreateMarker.emit(ev)
    }

    this.markerBeforeDeleteHandler = (ev: CustomEvent) => {
      this.stateDeleteMarker.emit(ev)
    }

    this.selectMarkerHandler = (ev: CustomEvent) => {
      this.stateSelectMarker.emit(ev)
    }

    // Add event listeners
    editor.markerArea.addEventListener('areastatechange', this.areaStateChangeHandler)
    editor.markerArea.addEventListener('markercreating', this.markerCreatingHandler)
    editor.markerArea.addEventListener('markerbeforedelete', this.markerBeforeDeleteHandler)
    editor.markerArea.addEventListener('markerselect', this.selectMarkerHandler)

    // Restore state in priority order:
    // 1. Current state (from previous instance)
    // 2. Input state
    // 3. Nothing (fresh start)
    const stateToRestore = this.currentState || this.state
    if (stateToRestore) {
      editor.restoreState(stateToRestore)
    }

    this.containerDivElement.nativeElement.appendChild(editor)
    this.markerArea = editor
    this.isInitialized = true

    this.whenRender.emit()
    this.cdr.detectChanges()
  }

  private saveCurrentState(): void {
    if (this.markerArea && this.isInitialized) {
      this.currentState = this.markerArea.markerArea.getState()
    }
  }

  private reloadMarker(): void {
    if (!this.isInitialized) {
      this.initializeMarker()
      return
    }

    this.saveCurrentState()

    this.destroyMarker()
    this.showImage = true
    this.cdr.detectChanges()

    setTimeout(() => {
      this.initializeMarker()
    }, 400)
  }
  private areaStateChangeHandler!: (ev: CustomEvent) => void
  private selectMarkerHandler!: (ev: CustomEvent) => void
  private markerCreatingHandler!: (ev: CustomEvent) => void
  private markerBeforeDeleteHandler!: (ev: CustomEvent) => void

  private setOptions(editor: AnnotationEditor): void {
    if (!this.options.toolbar.markers || !this.options.toolbar.markers.length) {
      return
    }

    const { markers, showCancelButton, showDoneButton } = this.options.toolbar
    const style = document.createElement('style')
    style.textContent = this.possibleMarkers
      .filter((m) => !markers?.includes(m))
      .map((marker) => `[title="${marker}"] { display: none !important; }`)
      .join('\n')

    if (!showCancelButton) {
      style.textContent += '[title="Close"] { display: none !important; }'
    }
    if (!showDoneButton) {
      style.textContent += '[title="OK"] { display: none !important; }'
    }

    editor.markerArea.appendChild(style)
  }

  protected showImage: boolean = true

  public reload(): void {
    this.reloadMarker()
  }

  public getCurrentState(): AnnotationState | null {
    return this.currentState
  }

  public updateState(newState: AnnotationState): void {
    this.currentState = newState
    if (this.isInitialized && this.markerArea) {
      this.markerArea.restoreState(newState)
    }
  }
}
